import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Key } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// Assets
import logo from "../../assets/MariTest_logomark.png";
import dashboardPreview from "../../assets/dashboard-preview.png";

const SignupPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [loadingSignup, setLoadingSignup] = useState(false);

  const navigate = useNavigate();
  const { signup } = useAuth();

  /* -----------------------------------------------------------
      Disable page scroll (clean UI)
  ----------------------------------------------------------- */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  /* -----------------------------------------------------------
      Handle Admin Signup
  ----------------------------------------------------------- */
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim() || !accessCode.trim()) {
      setError("All fields are required.");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setLoadingSignup(true);

    const success = await signup(email, password, accessCode);

    setLoadingSignup(false);

    if (!success) {
      setError("Signup failed — invalid admin access code or email already exists.");
      return;
    }

    navigate("/login");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: "#0b1120",
        position: "fixed",
        inset: 0,
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          flex: { xs: 1, md: "0 0 45%" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 3, sm: 6, md: 8 },
          py: { xs: 6, md: 0 },
          minHeight: "100vh",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Logo + Title */}
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <img
              src={logo}
              alt="MariTest Logo"
              style={{
                width: 100,
                marginBottom: "1.5rem",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />

            <Typography variant="h5" sx={{ fontWeight: 700, color: "#f9fafb" }}>
              MariTest Central
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#f9fafb",
                mb: 3,
              }}
            >
              Intelligence Dashboard
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: "#e5e7eb",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            Admin Signup
          </Typography>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "10px",
                backgroundColor: "rgba(239,68,68,0.1)",
                color: "#f87171",
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSignup}>
            {/* Email */}
            <TextField
              fullWidth
              label="Enter Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              variant="outlined"
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Password */}
            <TextField
              fullWidth
              label="Create Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              variant="outlined"
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: "#9ca3af" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Access Code */}
            <TextField
              fullWidth
              label="Admin Access Code"
              type={showAccessCode ? "text" : "password"}
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              autoComplete="off"
              variant="outlined"
              sx={{
                mb: 3,
                input: { color: "#f3f4f6" },
                label: { color: "#9ca3af" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#111827",
                  "& fieldset": { borderColor: "#1f2937" },
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Key sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAccessCode(!showAccessCode)}
                      sx={{ color: "#9ca3af" }}
                    >
                      {showAccessCode ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Signup Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loadingSignup}
              sx={{
                backgroundColor: "#3b82f6",
                py: 1.3,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(59,130,246,0.25)",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              {loadingSignup ? "Creating Account..." : "Sign Up"}
            </Button>

            {/* Login Redirect */}
            <Typography
              variant="body2"
              align="center"
              sx={{ mt: 2, color: "#cbd5e1" }}
            >
              Already have an account?{" "}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate("/login")}
                sx={{
                  fontWeight: 700,
                  color: "#3b82f6",
                  "&:hover": { color: "#2563eb" },
                }}
              >
                Login
              </Link>
            </Typography>
          </form>
        </Box>
      </Box>

      {/* Right Panel */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={dashboardPreview}
          alt="Dashboard Background"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(6px) brightness(0.35)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to left, rgba(11,17,32,0.95) 0%, rgba(11,17,32,0.7) 40%, rgba(11,17,32,0.2) 100%)",
          }}
        />

        <Box
          component="img"
          src={dashboardPreview}
          alt="Dashboard Foreground"
          sx={{
            width: "68%",
            maxWidth: 520,
            borderRadius: "16px",
            boxShadow:
              "0 15px 35px rgba(0,0,0,0.6), 0 0 40px rgba(37,99,235,0.25)",
            zIndex: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export default SignupPage;
